import React, { useMemo, useCallback, useState } from "react";
import mapValues from "lodash/mapValues";
import pick from "lodash/pick";
import omit from "lodash/omit";
import isEmpty from "lodash/isEmpty";
import styled from "styled-components";
import Store from "logic/local-storage/Store";
import { Modal, Button } from "components/bootstrap";
import CheckBoxGroup from "views/logic/valueactions/createEventDefinition/CheckBoxGroup";
import { aggregationGroup, searchGroup, labels } from "views/logic/valueactions/createEventDefinition/Constants";
import RadioSection from "views/logic/valueactions/createEventDefinition/RadioSection";
import { ExpandableList, Icon, ExpandableCheckboxListItem } from "components/common";
import useLocalStorageConfigData from "views/logic/valueactions/createEventDefinition/hooks/useLocalStorageConfigData";
import Routes from "routing/Routes";
import useModalReducer from "views/logic/valueactions/createEventDefinition/hooks/useModalReducer";
import generateId from "logic/generateId";
const Container = styled.div`
  margin-top: 10px;
`;
const CheckboxLabel = ({ itemKey, value }) => /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement("i", null, `${labels[itemKey]}: `), /* @__PURE__ */ React.createElement("b", null, value));
const SECTION_LABELS = {
  aggregation: "Aggregation settings",
  search: "Search query"
};
const CreateEventDefinitionModal = ({
  modalData,
  mappedData,
  show,
  onClose
}) => {
  var _a, _b;
  const [expandedSections, setExpandedSections] = useState(Object.values(SECTION_LABELS));
  const [{ strategy, checked, showDetails }, dispatchWithData] = useModalReducer(modalData);
  const localStorageConfig = useLocalStorageConfigData({ mappedData, checked });
  const sessionId = useMemo(() => `cedfv-${generateId()}`, []);
  const eventDefinitionCreationUrl = `${Routes.ALERTS.DEFINITIONS.CREATE}?step=condition&session-id=${sessionId}`;
  const onCheckboxChange = useCallback(
    (updates) => {
      dispatchWithData({ type: "UPDATE_CHECKED_ITEMS", payload: updates });
    },
    [dispatchWithData]
  );
  const onStrategyChange = useCallback(
    (e) => {
      dispatchWithData({ type: `SET_${e.target.value}_STRATEGY` });
    },
    [dispatchWithData]
  );
  const toggleDetailsOpen = useCallback(() => {
    dispatchWithData({ type: "TOGGLE_SHOW_DETAILS" });
  }, [dispatchWithData]);
  const aggregationChecks = useMemo(() => pick(checked, aggregationGroup), [checked]);
  const searchChecks = useMemo(() => pick(checked, searchGroup), [checked]);
  const restChecks = useMemo(() => omit(checked, [...searchGroup, ...aggregationGroup]), [checked]);
  const aggregationLabels = useMemo(
    () => mapValues(pick(modalData, aggregationGroup), (value, key) => /* @__PURE__ */ React.createElement(CheckboxLabel, { itemKey: key, value })),
    [modalData]
  );
  const searchLabels = useMemo(
    () => mapValues(pick(modalData, searchGroup), (value, key) => /* @__PURE__ */ React.createElement(CheckboxLabel, { itemKey: key, value })),
    [modalData]
  );
  const restLabels = useMemo(
    () => mapValues(omit(modalData, [...aggregationGroup, ...searchGroup]), (value, key) => /* @__PURE__ */ React.createElement(CheckboxLabel, { itemKey: key, value })),
    [modalData]
  );
  const strategyAvailabilities = useMemo(
    () => {
      var _a2, _b2;
      return {
        ALL: true,
        ROW: !!((_a2 = mappedData == null ? void 0 : mappedData.rowValuePath) == null ? void 0 : _a2.length),
        COL: !!((_b2 = mappedData == null ? void 0 : mappedData.columnValuePath) == null ? void 0 : _b2.length),
        CUSTOM: true,
        EXACT: true
      };
    },
    [(_a = mappedData == null ? void 0 : mappedData.columnValuePath) == null ? void 0 : _a.length, (_b = mappedData == null ? void 0 : mappedData.rowValuePath) == null ? void 0 : _b.length]
  );
  const onContinueConfigurationClick = useCallback(() => {
    Store.set(sessionId, localStorageConfig);
    onClose();
  }, [sessionId, localStorageConfig, onClose]);
  return /* @__PURE__ */ React.createElement(Modal, { onHide: onClose, show }, /* @__PURE__ */ React.createElement(Modal.Header, null, /* @__PURE__ */ React.createElement(Modal.Title, null, "Configure new event definition")), /* @__PURE__ */ React.createElement(Modal.Body, null, /* @__PURE__ */ React.createElement(RadioSection, { strategyAvailabilities, strategy, onChange: onStrategyChange }), /* @__PURE__ */ React.createElement(Button, { bsStyle: "link", className: "btn-text", bsSize: "xsmall", onClick: toggleDetailsOpen }, /* @__PURE__ */ React.createElement(Icon, { name: `arrow_${showDetails ? "drop_down" : "right"}` }), "\xA0", showDetails ? "Hide strategy details" : "Show strategy details"), showDetails && /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(
    ExpandableList,
    {
      value: expandedSections,
      onChange: (newExpandedSections) => setExpandedSections(newExpandedSections)
    },
    !isEmpty(aggregationChecks) && /* @__PURE__ */ React.createElement(
      CheckBoxGroup,
      {
        onChange: onCheckboxChange,
        groupLabel: SECTION_LABELS.aggregation,
        checked: aggregationChecks,
        labels: aggregationLabels
      }
    ),
    !isEmpty(searchChecks) && /* @__PURE__ */ React.createElement(
      CheckBoxGroup,
      {
        onChange: onCheckboxChange,
        groupLabel: SECTION_LABELS.search,
        checked: searchChecks,
        labels: searchLabels
      }
    ),
    Object.entries(restChecks).map(([key, isChecked]) => /* @__PURE__ */ React.createElement(
      ExpandableCheckboxListItem,
      {
        key,
        value: key,
        checked: isChecked,
        onChange: () => onCheckboxChange({ [key]: !isChecked }),
        header: restLabels[key],
        expandable: false
      }
    ))
  ))), /* @__PURE__ */ React.createElement(Modal.Footer, null, /* @__PURE__ */ React.createElement(
    Button,
    {
      bsStyle: "primary",
      onClick: onContinueConfigurationClick,
      href: eventDefinitionCreationUrl,
      target: "_blank"
    },
    "Continue configuration"
  )));
};
export default CreateEventDefinitionModal;
